import type { ReactNode } from 'react';
import { Avatar, Box, Chip, LinearProgress, Paper, Stack, Typography, type SxProps, type Theme } from '@mui/material';
import { isUploadedFileUrl, openFile } from './editor/fileViewer';
import { RichTextEditor } from './editor/RichTextEditor';
import { initials, PRIORITY_COLORS, sanitizeHtml } from '../lib/format';

export const BRAND_GRADIENT = 'linear-gradient(135deg, #6226A7 0%, #A855F7 55%, #F6A938 100%)';

/* ---------- Avatar ---------- */
const API_ORIGIN = (process.env.REACT_APP_BASE_URL || 'http://localhost:4000/api').replace(/\/api\/?$/, '');
export const fileUrl = (u?: string) => (u ? (/^https?:\/\//i.test(u) ? u : `${API_ORIGIN}${u.startsWith('/') ? '' : '/'}${u}`) : '');

export function UserAvatar({ name, src, size = 28 }: { name?: string; src?: string; size?: number }) {
  return (
    <Avatar src={fileUrl(src) || undefined} alt={name} sx={{ width: size, height: size, fontSize: size * 0.4, fontWeight: 700, bgcolor: '#6226A7', color: '#fff' }}>
      {initials(name)}
    </Avatar>
  );
}

export function AvatarStack({ users, max = 4, size = 26 }: { users: { name?: string; avatarUrl?: string }[]; max?: number; size?: number }) {
  const shown = users.slice(0, max);
  const extra = users.length - shown.length;
  return (
    <Stack direction="row" sx={{ '& > *': { ml: '-6px', border: '2px solid', borderColor: 'background.paper', borderRadius: '50%' }, '& > *:first-of-type': { ml: 0 } }}>
      {shown.map((u, i) => (
        <UserAvatar key={i} name={u.name} src={u.avatarUrl} size={size} />
      ))}
      {extra > 0 && (
        <Avatar sx={{ width: size, height: size, fontSize: size * 0.38, bgcolor: 'action.selected', color: 'text.primary' }}>+{extra}</Avatar>
      )}
    </Stack>
  );
}

/* ---------- Layout bits ---------- */
export function PageHero({ badge, title, subtitle, actions, icon }: { badge?: string; title: string; subtitle?: string; actions?: ReactNode; icon?: ReactNode }) {
  return (
    <Paper
      sx={{
        p: { xs: 2, md: 3 },
        mb: 2.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        flexWrap: 'wrap',
        background: (t) =>
          t.palette.mode === 'dark'
            ? 'linear-gradient(135deg, rgba(98,38,167,0.28), rgba(246,169,56,0.08))'
            : 'linear-gradient(135deg, #FFFFFF 0%, #F6EFFF 55%, #FFF4E6 100%)',
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        {icon && (
          <Box sx={{ width: 52, height: 52, display: 'grid', placeItems: 'center', color: '#fff', background: BRAND_GRADIENT, flexShrink: 0 }}>{icon}</Box>
        )}
        <Box>
          {badge && (
            <Chip size="small" label={badge} sx={{ mb: 0.75, fontWeight: 700, bgcolor: 'rgba(98,38,167,0.10)', color: 'primary.main' }} />
          )}
          <Typography variant="h4" sx={{ fontWeight: 800 }}>{title}</Typography>
          {subtitle && <Typography color="text.secondary" sx={{ mt: 0.25 }}>{subtitle}</Typography>}
        </Box>
      </Stack>
      {actions && <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>{actions}</Stack>}
    </Paper>
  );
}

export function EmptyBlock({ title, description, action, icon }: { title: string; description?: string; action?: ReactNode; icon?: ReactNode }) {
  return (
    <Paper sx={{ p: 5, textAlign: 'center' }}>
      {icon && <Box sx={{ color: 'primary.main', mb: 1, '& svg': { fontSize: 44 } }}>{icon}</Box>}
      <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
      {description && <Typography color="text.secondary" sx={{ mt: 0.5, mb: action ? 2 : 0 }}>{description}</Typography>}
      {action}
    </Paper>
  );
}

export function ColorDot({ color, size = 10 }: { color: string; size?: number }) {
  return <Box sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />;
}

export function PriorityChip({ priority }: { priority: string }) {
  const color = PRIORITY_COLORS[priority] || '#98A2B3';
  return <Chip size="small" label={priority.charAt(0).toUpperCase() + priority.slice(1)} sx={{ fontWeight: 700, bgcolor: `${color}1F`, color, height: 22 }} />;
}

export function StatusChip({ label, color }: { label: string; color: string }) {
  return (
    <Chip
      size="small"
      icon={<ColorDot color={color} size={8} />}
      label={label}
      sx={{ fontWeight: 700, bgcolor: `${color}1A`, color: 'text.primary', height: 24, '& .MuiChip-icon': { ml: '8px' } }}
    />
  );
}

export function ProgressBar({ value, color = '#6226A7', height = 8 }: { value: number; color?: string; height?: number }) {
  return (
    <LinearProgress
      variant="determinate"
      value={Math.min(100, Math.max(0, value))}
      sx={{ height, borderRadius: 1, bgcolor: 'action.hover', '& .MuiLinearProgress-bar': { bgcolor: color } }}
    />
  );
}

/* ---------- Rich text ---------- */
export { RichTextEditor };

/** Read-only rich text. Images open in a lightbox and uploaded files in the in-app reader (no new tab). */
export function RichTextView({ html, sx }: { html?: string; sx?: SxProps<Theme> }) {
  if (!html) return null;
  return (
    <Box
      onClick={(e) => {
        const target = e.target as HTMLElement;
        const img = target.closest('img') as HTMLImageElement | null;
        if (img) {
          e.preventDefault();
          openFile({ url: img.src, name: img.src.split('/').pop() });
          return;
        }
        const link = target.closest('a');
        if (link && isUploadedFileUrl(link.href)) {
          e.preventDefault();
          openFile({ url: link.href, name: link.textContent || undefined });
        }
      }}
      sx={{
        '& p': { m: 0, mb: 1 },
        '& ul, & ol': { pl: 3, my: 0.5 },
        '& a': { color: 'primary.main' },
        '& .mention': { fontWeight: 800, color: 'primary.main' },
        '& img': { maxWidth: '100%', height: 'auto', cursor: 'zoom-in' },
        wordBreak: 'break-word',
        ...(sx as object),
      }}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }}
    />
  );
}

